import {
  useCanCall,
  useCreateUser,
  useCurrentUser,
  useDeleteUser,
  useHistory,
  useLockUser,
  useMe,
  useResetPassword,
  useRevokeSession,
  useSessionId,
  useSessions,
  useUnlockUser,
  useUpdateUser,
  useUsers,
  useUpdateSessionPolicy,
  useUserSessionPolicy,
} from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { AUDIT_HISTORY, DEMO_NOW, SESSIONS, USER_ROWS, type UserRow } from '@ctcells/fixtures'
import { errorMessage, t } from '@ctcells/i18n'
import { lazy, Suspense, useState } from 'react'
import { useParams } from 'react-router'

import { gate } from '../access.ts'
import { useScreenAccess } from '../screenAccess.ts'
import { ScreenLoading, ScreenState } from '../ScreenState.tsx'

import { AdminScreen, type AdminScreenProps, type AdminTab } from './AdminScreen.tsx'
import { toUserRows } from './adminModel.ts'
import { oneField, policyFailure, USER_SESSION_POLICY_SHOWN } from './policyModel.ts'
import { UserDetailScreen, type UserAction } from './UserDetailScreen.tsx'
import type { UserSessionPolicyEdit } from './UserSessionPolicyCard.tsx'

/**
 * 관리(사용자) · 사용자 상세 컨테이너.
 *
 * 분류: **1. 실제로 연결** — `GET /users` 9건 · `GET /sessions` 63건(2026-09-17 실측).
 *
 * 🔴 **화면은 0줄도 안 바뀐다.** 훅을 부르고 props 로 내려줄 뿐이다.
 * 🔴 **갈리는 층은 `dataSourceFor('users')` 하나다.** demo 경로는 네트워크 훅을 부르지 않는다.
 *
 * ⚠ **`user_id` 에 UUID 와 문자열(`"dev-admin"`)이 섞여 있다**(실측). 타입을 좁히지 않는다 —
 *   그냥 문자열로 흘린다.
 * ⚠ 실물 역할은 계약 enum **3종**뿐이고 `service` 는 0건이다. 역할 선택기(알약 묶음)도 그 셋만 고르게 한다.
 * ⚠ `Session.client`(접속 기기)가 실물에 오지 않는다 → 상세의 「접속 기기」 열이 빈다(보고 대상 K).
 *
 * ✅ **상세 화면의 fixture 결박을 풀었다**(2026-09-17). 예전에는 `UserDetailScreen` 이 내부에서
 *   `USERS`(fixture)를 `username` 으로 찾아 `user_id` 를 얻어 세션을 걸렀고, 실물 `user_id` 가
 *   fixture 에 없어 **세션 표·최근 감사 표가 통째로 비었다.**
 *   → `UserRow` 가 `user_id` 를 나르고 화면은 fixture 를 아예 모른다. demo 값도 여기서 넘긴다.
 *
 * ⚠ 실물 `history.actor` 는 **user_id(UUID)** 다(목은 username). 화면이 두 축을 다 받아 거르지만
 *   **표시는 받은 값 그대로**다 — UUID 를 이름으로 바꾸려고 사용자 목록을 조인하지 않는다.
 *   원장이 사람 이름을 주게 하는 것은 서버 몫이다(보고 대상).
 */

/** 한 번에 받는 상한. 서버 최대 500 아래로 둔다 */
const PAGE_LIMIT = 200

/**
 * 사용자 상세의 세션 표가 받는 상한. 서버 최대(500)다 — 사용자별로 걸러 보이므로 넉넉히 받는다.
 * ⚠ 목록(`AdminLive`)은 세션을 받지 않는다(2026-09-30 「세션」 열 삭제 · `adminModel.ts` `toUserRows` 주석).
 */
const SESSION_LIMIT = 500

/**
 * @param tab 열린 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다(`adminTabs.ts`).
 *   화면이 `useState` 로 들면 새로고침·상세 복귀에서 탭이 흔들린다.
 * @param adminTabs 탭 줄에 실을 목록. 관점마다 다르다 — 안 넘기면 넷(사용자·일반·모듈·로그)이다.
 *   장비 관점 콘솔은 모듈·로그가 사이드바에 따로 있어서 둘짜리를 넘긴다(`AdminScreenProps` 주석).
 */
export function AdminContainer(props: AdminTabProps = {}) {
  return dataSourceFor('users') === 'demo' ? <AdminDemo {...props} /> : <AdminLive {...props} />
}

/** 관점이 갈리는 값만 모은 것 — 데이터·권한은 아래 두 경로가 똑같이 넣는다 */
interface AdminTabProps {
  tab?: AdminTab | undefined
  adminTabs?: AdminScreenProps['adminTabs'] | undefined
  /**
   * 오른쪽 판에 열린 계정(`username`). 그 행을 «고른 행»으로 그린다.
   * ⚠ `members` 탭에서만 뜻이 있다 — 다른 탭에는 사용자 표가 없어 그냥 지나간다.
   */
  selectedUserId?: string | undefined
}

/**
 * 넘기지 «않는» 것과 `undefined` 를 넘기는 것이 다르다 — 기본값이 살아야 하므로 키째 뺀다.
 * 🔴 설정 탭에 꽂던 절(걸린 것 · 정책 · 알림 수신자 …)은 2026-10-14 에 걷었다(eric 「여기 다 안 쓸거야」 · `GeneralSettings` 머리말).
 */
/**
 * «알람 설정»은 설정 탭을 열 때만 받는다 — 에러 코드 사전 생성물(약 260KB)이 그 덩어리에 들어 있어 콘솔 첫 화면을 무겁게 하지 않는다.
 */
const AlarmSettingsContainer = lazy(() =>
  import('./AlarmSettingsContainer.tsx').then((m) => ({ default: m.AlarmSettingsContainer })),
)

const tabProps = ({ tab, adminTabs }: AdminTabProps) => ({
  ...(tab === undefined ? {} : { tab }),
  ...(adminTabs === undefined ? {} : { adminTabs }),
  /*
   * 🔴 웹훅 탭의 «알람 설정»(등록된 웹훅 + 알람별 전달 · eric 2026-10-28)은 **통합 콘솔에서만** 넣는다 — 제 탭 목록(`adminTabs`)을
   *    넘기는 장비 관점 콘솔은 슬롯을 안 받는다(그 앱은 고치지 않는다 · CLAUDE.md). 웹훅 탭일 때만 마운트한다 — 다른 탭에서 조회가 나가지 않게.
   * 🔴 설정 탭은 빈 판 — 안내 한 줄(내용은 eric 이 곧 요청 · 2026-10-28). 장비 관점 콘솔의 설정 탭은 종전 그대로(슬롯 없음).
   */
  ...(adminTabs === undefined && tab === 'general'
    ? { settings: <ScreenState text={t('admin.general.empty')} /> }
    : {}),
  ...(adminTabs === undefined && tab === 'webhooks'
    ? {
        settings: (
          <Suspense fallback={<ScreenLoading />}>
            <AlarmSettingsContainer />
          </Suspense>
        ),
      }
    : {}),
})

/** demo 경로는 목록 props 를 안 넘긴다 = 화면 기본값(`USER_ROWS`)이 그대로 산다 */
function AdminDemo({ selectedUserId, ...props }: AdminTabProps) {
  const access = useScreenAccess()
  return <AdminScreen access={access} selectedUserId={selectedUserId} {...tabProps(props)} />
}

function AdminLive({ tab, adminTabs, selectedUserId }: AdminTabProps) {
  const access = useScreenAccess()
  /*
   * 🔴 **설정 탭에서는 사용자·세션을 부르지 않는다**(2026-09-22). 둘 다 `ALLOWED_ROLES = {admin}`
   *    이라(`users.py:49` · `sessions.py:49`) 문이 `monitor` 로 내려간 지금 operator·viewer 가
   *    설정 탭을 열면 **보이지도 않는 표 때문에 403 이 두 번** 난다. 게다가 아래 `isError` 분기가
   *    화면을 통째로 사유 한 줄로 갈아 끼워 **설정이 통째로 사라진다** — 2026-09-21 에 내 계정이
   *    정확히 그 사고를 냈다(admin 전용 `GET /sessions` 의 403 이 화면을 덮었다).
   * ⚠ 「부가 조회의 실패가 주 화면을 죽이면 안 된다」의 같은 얼굴이다. 여기서는 그 탭에
   *   **주 자료가 아예 아니라서** 덮는 대신 «안 부른다».
   */
  /*
   * 🔴 **admin 이 아니면 `GET /users` 를 쏘지 않는다**(2026-09-30). 그 경로는 admin 전용이라
   *    (`users.py ALLOWED_ROLES` · `test/roleAccess.ts`) operator · viewer 는 403 이다. 사이드바 「관리」가
   *    **이 탭(`PATHS.admin`)으로** 들어와서, 종전에는 그 403 이 아래 `isError` 분기로 화면을 통째로 한 줄로
   *    갈아 끼워 **탭 줄까지 사라졌다** — 모듈 · 로그로 갈 길이 없었다(「부가 조회의 실패가 주 화면을 죽인다」).
   *    판정은 서버가 준 연산 목록(`/auth/me` 의 `listUsers`)이다 — `useCanCall`. 모르면(null) 쏘지 않는다.
   */
  const canList = useCanCall('listUsers')
  const { operations } = useMe()
  const skip = tab === 'general' || tab === 'webhooks' || !canList
  const users = useUsers({ limit: PAGE_LIMIT, skip })
  /*
   * 🔴 **목록은 `/sessions` 를 부르지 않는다**(2026-09-30). 「세션」 열(사용자당 세션 수)만 쓰던
   *    조회였고 그 열이 없어졌다(eric). 「마지막 로그인」은 `User.last_login_at` 이라 세션이 필요 없다.
   *    서랍의 세션 표는 `UserDetailLive` 가 제가 부른다.
   */
  const createUser = useCreateUser()

  /*
   * 🔴 **실패는 표 «안»에만 선다** — 화면을 갈아 끼우지 않는다. 탭 줄 · 다른 탭은 그대로다.
   * ⚠ 연산 목록을 받았는데 `listUsers` 가 없으면 「admin 만」 사유다(요청을 안 쏘았으니 실패도 없다 —
   *   비워 두면 「사용자 없음」으로 읽힌다). 연산 목록을 아직 모르면 불러오는 중이다.
   */
  const usersNotice = users.isError
    ? errorMessage(users.error)
    : !canList && operations !== null
      ? t('reason.usersAdminOnly')
      : undefined
  const loading = tab === 'members' && (canList ? users.isPending : operations === null)

  return (
    <AdminScreen
      access={access}
      creating={createUser.isPending}
      {...tabProps({ tab, adminTabs })}
      loading={loading}
      selectedUserId={selectedUserId}
      users={toUserRows(users.data?.items ?? [])}
      usersNotice={usersNotice}
      {...(createUser.isError ? { createError: errorMessage(createUser.error) } : {})}
      // 🔴 낙관적 갱신 없음. 목록에 새 계정이 뜨는 것은 무효화 뒤 서버 재조회 결과다
      onCreate={(input) => {
        createUser.mutate(input)
      }}
    />
  )
}

export interface UserDetailContainerProps {
  /**
   * 서랍(사용자 목록 오른쪽 판)으로 열렸을 때의 닫기. 없으면 전체 화면이고 「← 사용자」 줄이다.
   * 🔴 **옵션이어야 한다** — 장비 관점 콘솔(`apps/console-equipment`)이 prop 없이 전체 화면으로 쓴다.
   */
  onClose?: (() => void) | undefined
}

export function UserDetailContainer(props: UserDetailContainerProps = {}) {
  return dataSourceFor('users') === 'demo' ? (
    <UserDetailDemo {...props} />
  ) : (
    <UserDetailLive {...props} />
  )
}

/** demo 값도 **컨테이너가** 넘긴다 — 화면은 fixture 를 import 하지 않는다 */
function UserDetailDemo({ onClose }: UserDetailContainerProps) {
  const access = useScreenAccess()
  // 🔴 demo 에서도 「내가 누구인가」는 세션에서 온다. 목 값으로 지어내지 않는다
  const me = useCurrentUser()
  const sessionId = useSessionId()
  return (
    <UserDetailScreen
      access={access}
      audits={AUDIT_HISTORY}
      currentUserId={me?.user_id}
      // demo 의 «지금»은 fixture 시계다 — 목 세션은 그 시각 기준으로 지어졌다(실시계로 재면 전부 지난 것이 된다)
      now={Date.parse(DEMO_NOW)}
      onClose={onClose}
      sessions={SESSIONS}
      users={USER_ROWS}
      {...(sessionId === null ? {} : { currentSessionId: sessionId })}
      // demo 는 네트워크를 타지 않는다. 확인 대화까지 같은 경로를 밟고 거기서 끝난다 —
      // 목 데이터를 손으로 고쳐 «성공한 척» 하지 않는다(실물인 척 보이는 것이 더 나쁘다)
      onAction={() => undefined}
    />
  )
}

function UserDetailLive({ onClose }: UserDetailContainerProps) {
  const access = useScreenAccess()
  const me = useCurrentUser()
  /*
   * 🔴 admin 이 아니면 셋 다 쏘지 않는다(`GET /users` · `GET /sessions` 는 admin 전용 — 목록 컨테이너와 같은 판정).
   *    주소로 `/admin/members/:id` 에 들어와도 403 셋을 만들지 않고 서랍에 사유 한 줄만 선다.
   */
  const canList = useCanCall('listUsers')
  const { operations } = useMe()
  const skip = !canList
  const users = useUsers({ limit: PAGE_LIMIT, skip })
  const sessions = useSessions({ limit: SESSION_LIMIT, skip })
  /*
   * 실효 세션 만료(IFC-RO-89 · admin 한정). 경로는 **`user_id`** 를 받는데 주소의 `:userId` 는 `username` 이다 —
   * 받은 목록에서 그 행을 찾아 `user_id` 를 얻는다(화면과 같은 판정 · `UserDetailScreen` `users.find`).
   * 🔴 훅은 이른 반환보다 먼저 부른다. 행을 아직 모르면 `enabled` 가 막는다.
   */
  const { userId: routeUser } = useParams()
  const selected = users.data?.items.find((u) => u.username === routeUser)
  // 「세션 만료」 카드가 숨은 동안(`USER_SESSION_POLICY_SHOWN`)은 부르지 않는다
  const sessionPolicy = useUserSessionPolicy({
    userId: selected?.user_id,
    skip: skip || !USER_SESSION_POLICY_SHOWN,
  })
  /*
   * 그 계정 단 넣기 · 지우기(IFC-RO-88 · `07` §4.7 ⓑ). 🔴 **병합이다** — 고친 칸 하나만 싣는다(`null` = 지움).
   * 성공하면 훅이 `keys.users.all()` 을 무효화해 위 실효 값이 서버에서 다시 온다(낙관적 갱신 없음).
   */
  const updatePolicy = useUpdateSessionPolicy()
  const [policyFailures, setPolicyFailures] = useState(0)
  // 감사 원장. ⚠ 실물 `actor` 는 user_id(UUID)다 — 화면이 user_id·username 두 축으로 거른다
  const audits = useHistory({ limit: PAGE_LIMIT, event_type: 'audit', skip })
  /*
   * 만료 판정의 «지금» — **렌더 때 한 번** 잡는다(`useState` 초기값이라 다시 그려도 안 흐른다).
   * 🔴 주기 재계산 · 폴링 없음(DP-01). 무효화 뒤 재조회로 표가 바뀌어도 같은 시각으로 잰다 — 서랍을 다시 열면 새로 잡힌다.
   */
  const [now] = useState(() => Date.now())
  const sessionId = useSessionId()
  const updateUser = useUpdateUser()
  /**
   * 🔴 계정 조작 훅. 전부 `createCommand` 경유라 **성공하면 하는 일이 무효화 하나**다 —
   *    화면이 캐시를 손댈 구멍이 타입상 없다(낙관적 갱신 금지 · DP-04).
   * ⚠ 「비활성」은 `useDeleteUser`(= `DELETE /users/{id}` soft-delete)가 맡는다.
   *   계약 경로가 하나뿐이라 「삭제」 버튼을 따로 두지 않는다(eric 확정 2026-09-18).
   */
  const deactivateUser = useDeleteUser()
  const lockUser = useLockUser()
  const unlockUser = useUnlockUser()
  const resetPassword = useResetPassword()
  const revokeSession = useRevokeSession()
  /**
   * 마지막으로 보낸 조작. 🔴 **실패 문구는 그 조작의 것만 낸다.** 종전에는 「실패한 훅 중 첫 것」을
   * 골라 앞서 잠금이 남긴 실패가 뒤의 비밀번호 재설정 창에 떴다(react-query 는 다른 훅의 오류를 안 지운다).
   */
  const [last, setLast] = useState<UserAction['kind'] | null>(null)

  if (!canList && operations !== null) return <ScreenState text={t('reason.usersAdminOnly')} />
  if (users.isPending) return <ScreenLoading />
  if (users.isError) return <ScreenState text={errorMessage(users.error)} tone="error" />

  const rows = toUserRows(users.data.items)

  // 한 번에 하나만 돈다. 어느 것이든 떠 있으면 확인 버튼을 막고, 실패한 것의 문구를 보인다
  const byKind = {
    deactivate: deactivateUser,
    lock: lockUser,
    unlock: unlockUser,
    password: resetPassword,
    revokeSession,
    role: updateUser,
  } as const
  const commands = [deactivateUser, lockUser, unlockUser, resetPassword, revokeSession, updateUser]
  const failed = last === null ? undefined : byKind[last]
  const actionError = failed?.isError === true ? errorMessage(failed.error) : undefined
  /*
   * 🔴 역할 거절은 **역할 줄에** 선다(창을 닫아도 남는다). 코드 원문을 같이 적는다 —
   *    `LAST_ADMIN_PROTECT` 가 한글 문구만으로는 무엇이 막았는지 찾기 어렵다(eric 2026-09-21 「코드 일단 노출」).
   */
  const roleError = updateUser.isError ? withCode(updateUser.error) : undefined

  /**
   * 확인까지 끝난 조작을 계약 경로로 보낸다.
   *
   * 🔴 **성공 후 이동하지 않는다.** 비활성은 soft-delete 라 계정이 목록에 **남고**
   *    `status` 만 `disabled` 로 바뀐다(계약 200 = 비활성 반영본). 사라지지도 않는데 상세에서
   *    튕겨 나가면 이상하다 — 무효화로 이 화면의 배지가 「비활성」으로 바뀌는 것이 맞는 결과다.
   */
  /** 「세션 만료」 카드의 계정 단 넣기 · 지우기 — 계정을 아직 못 찾았으면 칸 없이 보기만 */
  const policyEdit: UserSessionPolicyEdit | undefined =
    selected === undefined
      ? undefined
      : {
          gate: updatePolicy.isPending
            ? { disabled: true, disabledReason: t('reason.requestInFlight') }
            : gate(access, { perm: 'settings' }),
          failures: policyFailures,
          ...(updatePolicy.isError ? { error: policyFailure(updatePolicy.error) } : {}),
          onCommit: (field, value) => {
            updatePolicy.mutate(
              { scope: `user:${selected.user_id}`, change: oneField(field, value) },
              { onError: () => setPolicyFailures((n) => n + 1) },
            )
          },
        }

  const run = (row: UserRow, action: UserAction) => {
    const targetId = row.user_id
    setLast(action.kind)
    switch (action.kind) {
      case 'deactivate':
        deactivateUser.mutate({ userId: targetId, reason: action.reason })
        return
      case 'lock':
        lockUser.mutate({ userId: targetId })
        return
      case 'unlock':
        unlockUser.mutate({ userId: targetId })
        return
      case 'password':
        // 관리자 강제 재설정이라 `current_password` 를 싣지 않는다(본인 재설정은 화면이 막는다)
        resetPassword.mutate({ userId: targetId, newPassword: action.newPassword })
        return
      case 'revokeSession':
        revokeSession.mutate({ sessionId: action.sessionId })
        return
      case 'role':
        /*
         * 🔴 **전체 교체라 받은 `username`·`name` 을 그대로 싣고 역할만 바꾼다**(`useUpdateUser` 머리말).
         *    빠뜨리면 422, 이름을 새로 지으면 조용히 덮인다.
         */
        updateUser.mutate({
          userId: targetId,
          username: row.username,
          name: row.name,
          role: action.role,
        })
    }
  }

  return (
    <UserDetailScreen
      access={access}
      audits={audits.data?.items ?? []}
      currentUserId={me?.user_id}
      now={now}
      onClose={onClose}
      pending={commands.some((c) => c.isPending)}
      {...(USER_SESSION_POLICY_SHOWN
        ? {
            sessionPolicy: sessionPolicy.isError
              ? { status: 'error' as const, text: errorMessage(sessionPolicy.error) }
              : sessionPolicy.data === undefined
                ? { status: 'loading' as const }
                : { status: 'ready' as const, policy: sessionPolicy.data },
            sessionPolicyEdit: policyEdit,
          }
        : {})}
      sessions={sessions.data?.items ?? []}
      users={rows}
      {...(sessionId === null ? {} : { currentSessionId: sessionId })}
      {...(actionError === undefined ? {} : { actionError })}
      {...(roleError === undefined ? {} : { roleError })}
      // 🔴 낙관적 갱신이 아니다. 화면이 바뀌는 것은 전부 무효화 뒤 서버 재조회 결과다
      onAction={run}
    />
  )
}

/** 에러 코드 사전 문구 + 코드 원문. 코드가 없으면 문구만 */
function withCode(error: Parameters<typeof errorMessage>[0]): string {
  const text = errorMessage(error)
  const code = error?.error_code
  return code === undefined ? text : `${text} · ${code}`
}
